import type { AttendanceEntry, AttendanceSession } from "../types";

// State of the check-in page (#16): what to show before the tap, and how the RPC answers map to views.

export const TIME_ZONE = "Europe/Berlin";
const WINDOW_MS = 30 * 60 * 1000;

export type CheckInSession = Pick<
  AttendanceSession,
  "id" | "title" | "starts_at" | "ends_at"
> &
  Partial<
    Pick<AttendanceSession, "location" | "cancelled" | "district" | "billable">
  >;

export type OwnEntry = Pick<AttendanceEntry, "status" | "checked_in_at"> & {
  session: CheckInSession;
};

export type CheckInView =
  | { kind: "ready"; session: CheckInSession | null }
  | { kind: "no_code"; session: CheckInSession | null }
  | { kind: "too_early"; session: CheckInSession; opensAt: string }
  | { kind: "window_over"; session: CheckInSession }
  | { kind: "cancelled"; session: CheckInSession }
  | {
      kind: "already";
      session: CheckInSession | null;
      title: string;
      checkedInAt: string;
    }
  | {
      kind: "checked_in";
      session: CheckInSession | null;
      title: string;
      checkedInAt: string;
    }
  | {
      kind: "invalid_code";
      session: CheckInSession | null;
      attemptsLeft: number;
    }
  | { kind: "too_many_attempts"; retryAfter: string | null }
  | { kind: "not_invited"; session: CheckInSession | null }
  | { kind: "requested"; session: CheckInSession }
  | { kind: "error"; message: string };

const fmt = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("de-DE", { timeZone: TIME_ZONE, ...options });

export const formatTime = (iso: string) =>
  fmt({ hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

/** Weekday, date and time span in Berlin time, as on the session card. */
export const formatSessionWhen = (s: CheckInSession) => {
  const start = new Date(s.starts_at);
  const day = fmt({ weekday: "short" }).format(start).replace(/\.$/, "");
  const date = fmt({
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(start);
  return `${day} ${date} · ${formatTime(s.starts_at)}–${formatTime(s.ends_at)}`;
};

const berlinDay = (d: Date) =>
  fmt({ year: "numeric", month: "2-digit", day: "2-digit" }).format(d);

/** Same window as the server: from 30 minutes before the start to 30 minutes after the end. */
export const checkInWindow = (s: CheckInSession) => ({
  opens: new Date(new Date(s.starts_at).getTime() - WINDOW_MS),
  closes: new Date(new Date(s.ends_at).getTime() + WINDOW_MS),
});

/** Today's own entry (Berlin day): the open window first, then the next one, then the last one. */
export const pickTodayEntry = (entries: OwnEntry[], now: Date) => {
  const byStart = entries
    .filter((e) => berlinDay(new Date(e.session.starts_at)) === berlinDay(now))
    .sort((a, b) => a.session.starts_at.localeCompare(b.session.starts_at));
  const at = now.getTime();
  const open = byStart.find((e) => {
    const w = checkInWindow(e.session);
    return w.opens.getTime() <= at && at <= w.closes.getTime();
  });
  const next = byStart.find(
    (e) => checkInWindow(e.session).opens.getTime() > at,
  );
  return open ?? next ?? byStart.at(-1) ?? null;
};

const timing = (session: CheckInSession, now: Date): CheckInView | null => {
  const w = checkInWindow(session);
  if (now < w.opens) {
    return { kind: "too_early", session, opensAt: w.opens.toISOString() };
  }
  if (now > w.closes) return { kind: "window_over", session };
  return null;
};

/** Before the tap. Without an own entry the button still shows; the server decides. */
export const initialView = (
  code: string | null,
  entry: OwnEntry | null,
  now: Date,
): CheckInView => {
  const session = entry?.session ?? null;
  if (session?.cancelled) return { kind: "cancelled", session };
  if (entry?.status === "present" && entry.checked_in_at) {
    return {
      kind: "already",
      session,
      title: session?.title ?? "",
      checkedInAt: entry.checked_in_at,
    };
  }
  if (!code) return { kind: "no_code", session };
  return (session && timing(session, now)) ?? { kind: "ready", session };
};

export type CheckInResult =
  | {
      result: "checked_in" | "already_checked_in";
      session_id: number;
      title: string;
      checked_in_at: string;
    }
  | { result: "invalid_code"; attempts_left: number };

/** Answer of attendance_check_in. */
export const checkInResultView = (
  data: CheckInResult,
  known: CheckInSession | null,
): CheckInView => {
  if (data.result === "invalid_code") {
    return {
      kind: "invalid_code",
      session: known,
      attemptsLeft: Math.max(0, data.attempts_left),
    };
  }
  const session = known?.id === data.session_id ? known : null;
  return {
    kind: data.result === "checked_in" ? "checked_in" : "already",
    session,
    title: data.title,
    checkedInAt: data.checked_in_at,
  };
};

export type RpcError = { message?: string; details?: string | null };

const parseDetail = (details: string | null | undefined) => {
  if (!details) return null;
  try {
    const parsed = JSON.parse(details);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const detailSession = (
  detail: Record<string, unknown> | null,
  known: CheckInSession | null,
): CheckInSession | null => {
  if (!detail || typeof detail.session_id !== "number") return known;
  if (known?.id === detail.session_id) return known;
  return {
    id: detail.session_id,
    title: String(detail.title ?? ""),
    starts_at: String(detail.starts_at ?? ""),
    ends_at: String(detail.ends_at ?? ""),
  };
};

/** Error of attendance_check_in; codes and DETAIL come from private.attendance_check_in. */
export const checkInErrorView = (
  error: RpcError,
  known: CheckInSession | null,
  now: Date,
): CheckInView => {
  const message = error.message ?? "";
  const detail = parseDetail(error.details);
  const session = detailSession(detail, known);
  if (message.includes("too_many_attempts")) {
    const retry = detail?.retry_after;
    return {
      kind: "too_many_attempts",
      retryAfter: typeof retry === "string" ? retry : null,
    };
  }
  if (message.includes("not_invited")) return { kind: "not_invited", session };
  if (message.includes("session_cancelled") && session) {
    return { kind: "cancelled", session };
  }
  if (message.includes("outside_window") && session) {
    return timing(session, now) ?? { kind: "window_over", session };
  }
  if (message.includes("not_signed_in")) {
    return { kind: "error", message: "Bitte erneut anmelden." };
  }
  return {
    kind: "error",
    message: "Das hat nicht geklappt. Bitte noch einmal versuchen.",
  };
};

/** Session for "Nachtrag anfragen": only where it helps and once the window has opened. */
export const lateRequestSession = (
  view: CheckInView,
  now: Date,
): CheckInSession | null => {
  if (
    view.kind !== "no_code" &&
    view.kind !== "window_over" &&
    view.kind !== "not_invited"
  ) {
    return null;
  }
  const s = view.session;
  if (!s || !s.starts_at || s.cancelled) return null;
  return now >= checkInWindow(s).opens ? s : null;
};

/** Text for an error of attendance_request_late. */
export const lateRequestErrorText = (error: RpcError) => {
  const message = error.message ?? "";
  const known: Record<string, string> = {
    too_early: "Ein Nachtrag geht erst ab 30 Minuten vor Beginn.",
    already_present: "Du bist schon eingetragen.",
    session_cancelled: "Die Sitzung ist abgesagt.",
    not_allowed:
      "Für diese Sitzung geht kein Nachtrag. Bitte das Fraktionsbüro fragen.",
  };
  const hit = Object.keys(known).find((k) => message.includes(k));
  return hit
    ? known[hit]
    : "Das hat nicht geklappt. Bitte noch einmal versuchen.";
};

/** Room code as the server compares it. */
export const normalizeCode = (code: string | undefined | null) => {
  const c = (code ?? "").trim().toUpperCase();
  return c === "" ? null : c;
};
